import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hospital } from "lucide-react";

export default function RefundPolicy() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="rounded-lg bg-blue-600 p-2">
              <Hospital className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">AMCARE</h1>
          </Link>
          <Button variant="outline" asChild>
            <Link to="/login">Login</Link>
          </Button>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Cancellation & Refund Policy</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Subscription Cancellation</h2>
              <p className="text-gray-700 mb-2">
                You may cancel your subscription at any time through your account settings. 
                Cancellation will take effect at the end of your current billing period.
              </p>
              <p className="text-gray-700">
                Once cancelled, you will continue to have access to the service until the end of 
                your paid subscription period. No partial refunds will be provided for the 
                remaining period.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Refund Policy</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold mb-2">Eligible for Refund:</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Refund requests made within 7 days of initial subscription payment</li>
                    <li>Technical issues preventing service access that cannot be resolved</li>
                    <li>Duplicate payments made in error</li>
                    <li>Service discontinuation by AMCare</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Not Eligible for Refund:</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Requests made after 7 days of subscription payment</li>
                    <li>Change of mind after using the service</li>
                    <li>Non-usage of the service</li>
                    <li>Violation of terms of service</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Refund Process</h2>
              <p className="text-gray-700 mb-2">
                To request a refund, please contact us at <a href="mailto:support@amcares.in" className="text-blue-600 hover:underline">support@amcares.in</a> 
                with your subscription details and reason for refund request.
              </p>
              <p className="text-gray-700">
                Refunds will be processed within 7-14 business days to the original payment method 
                used for the transaction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Payment Method</h2>
              <p className="text-gray-700">
                Refunds will be credited to the same payment method (bank account, credit card, 
                debit card, or UPI) that was used for the original transaction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Contact Us</h2>
              <p className="text-gray-700">
                For any questions regarding cancellations or refunds, please contact us:
              </p>
              <div className="mt-2 space-y-1 text-gray-700">
                <p>Email: <a href="mailto:support@amcares.in" className="text-blue-600 hover:underline">support@amcares.in</a></p>
                <p>Visit: <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us Page</a></p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>&copy; {new Date().getFullYear()} AMCare. All rights reserved.</p>
          <div className="mt-2 flex justify-center gap-4">
            <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:underline">Terms & Conditions</Link>
            <Link to="/contact-us" className="hover:underline">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

